<template>
  <div class="tw-prose__features tw-prose">
    <div ref="lightboxRef">
      <component :is="{ render: () => nodes.map(getVNode) }" />
    </div>
  </div>
</template>

<script setup>
import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import { computed, h, onMounted, useTemplateRef } from "vue";

const props = defineProps({
  content: {
    type: String,
    default: "",
  },
});

// Template ref
const lightboxRef = useTemplateRef("lightboxRef");

const LIGHTBOX_NODE_TYPES = ["img"];

const parseNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tag = node.tagName.toLowerCase();
  const component = tag;

  const props = {};
  for (const attr of node.attributes) {
    props[attr.name] = attr.value;
  }

  if (LIGHTBOX_NODE_TYPES.includes(tag)) {
    props["data-lightbox"] = props.src;
    props["data-external-thumb-image"] = props.src;

    // Classes
    props.class = "tw-cursor-pointer";
  }

  const children = [];
  node.childNodes.forEach((child) => {
    const parsed = parseNode(child);

    if (parsed !== null && parsed !== undefined && parsed !== "") {
      children.push(parsed);
    }
  });

  return { component, props, children };
};

const getVNode = (node) => {
  if (typeof node === "string") {
    return node;
  }

  if (!node) {
    return null;
  }

  return h(
    node.component,
    node.props,
    node.children ? node.children.map(getVNode) : undefined,
  );
};

const nodes = computed(() => {
  if (!props.content) {
    return [];
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(props.content, "text/html");

  return Array.from(parsed.body.childNodes).map(parseNode).filter(Boolean);
});

onMounted(() => {
  lightGallery(lightboxRef.value, {
    licenseKey: import.meta.env?.VITE_LIGHTGALLERY_LICENSE_KEY,
    plugins: [lgThumbnail, lgZoom],
    speed: 350,
    selector: "[data-lightbox]",
    exThumbImage: "data-external-thumb-image",
    addClass: "light-gallery",
  });
});
</script>
